import React, { useEffect, useState } from 'react';
import { DatePicker, DayOfWeek, defaultDatePickerStrings } from '@fluentui/react';
import { Button, MessageBarBody, MessageBarTitle } from '@fluentui/react-components';
import { Checkmark20Regular, Dismiss20Regular, Edit20Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';

import { IAssociatedPriceList } from '../../../models/Entities/PriceList/IAssociatedPriceList';
import { onFormatDate, parseUTCDateAsLocal } from '../../../commons/functions/dateUtils';

// guía
import { StatusPill } from '../../../theme/components/StatusPill';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import { GuideTooltip } from '../../../theme/components/GuideTooltip';

// lógica
import { daysToExpiration, isValidityRangeValid, priceListStatus } from '../PriceList.Logic';

import './PriceListInfoComponent.css';

export interface PriceListInfoPanelProps {
  priceList: IAssociatedPriceList | undefined;
  onConfirmStartValidity: (date: Date) => void;
  onConfirmEndValidity: (date: Date) => void;
  /** Raíz de traducciones: 'priceListInfo' | 'providerPriceListInfo' | 'tenantPriceListInfo' */
  i18nRoot?: string;
}

type Side = 'start' | 'end';

/**
 * Vigencia de la lista (guía): tarjeta con título de sección, "Desde" y "Hasta" como campos
 * de sólo lectura con lápiz para editar en línea (confirmar / cancelar), estado como etiqueta
 * y el vencimiento en días. El error de rango va como banner dentro de la tarjeta.
 */
export const PriceListInfoPanel = (props: PriceListInfoPanelProps) => {
  const { priceList, i18nRoot = 'priceListInfo' } = props;
  const [t] = useTranslation();

  const [editing, setEditing] = useState<Side | undefined>(undefined);
  const [draft, setDraft] = useState<Date | undefined>(undefined);
  const [rangeError, setRangeError] = useState(false);

  // Al cambiar de lista se cierra cualquier edición a medias.
  useEffect(() => {
    setEditing(undefined);
    setDraft(undefined);
    setRangeError(false);
  }, [priceList?.id]);

  if (!priceList) return null;

  const start = parseUTCDateAsLocal(priceList.effectiveDateStart);
  const end = parseUTCDateAsLocal(priceList.effectiveDateEnd);
  const status = priceListStatus(priceList);
  const days = daysToExpiration(priceList);

  const startEdit = (side: Side) => {
    setEditing(side);
    setDraft(side === 'start' ? start : end);
    setRangeError(false);
  };

  const cancel = () => {
    setEditing(undefined);
    setDraft(undefined);
    setRangeError(false);
  };

  const confirm = () => {
    if (!editing || !draft) return;
    const valid = editing === 'start' ? isValidityRangeValid(draft, end) : isValidityRangeValid(start, draft);
    if (!valid) { setRangeError(true); return; }
    if (editing === 'start') props.onConfirmStartValidity(draft); else props.onConfirmEndValidity(draft);
    cancel();
  };

  const expirationText = days === undefined
    ? t(`${i18nRoot}.undetermined`)
    : days > 0 ? t('priceListPanel.expiresIn', { count: days })
    : days === 0 ? t('priceListPanel.expiresToday')
    : t('priceListPanel.expiredAgo', { count: Math.abs(days) });

  const renderDateField = (side: Side, label: string, value: Date | undefined, placeholder: string) => {
    const isEditing = editing === side;
    return (
      <div className={`pli-field${isEditing ? ' is-editing' : ''}`} data-testid={`pli-${side}`}>
        <span className="pli-field__label">{label}</span>
        {isEditing ? (
          <div className="pli-field__edit">
            <DatePicker
              className="pli-field__picker"
              firstDayOfWeek={DayOfWeek.Sunday}
              placeholder={placeholder}
              ariaLabel={label}
              formatDate={onFormatDate}
              value={draft}
              onSelectDate={(date) => setDraft(date || undefined)}
              strings={defaultDatePickerStrings}
            />
            <GuideTooltip content={t(`${i18nRoot}.parameters.confirmButton`)} relationship="label">
              <Button appearance="primary" size="small" icon={<Checkmark20Regular />} onClick={confirm} disabled={!draft} data-testid={`pli-${side}-confirm`} />
            </GuideTooltip>
            <GuideTooltip content={t(`${i18nRoot}.parameters.cancelButton`)} relationship="label">
              <Button appearance="subtle" size="small" icon={<Dismiss20Regular />} onClick={cancel} />
            </GuideTooltip>
          </div>
        ) : (
          <div className="pli-field__row">
            <span className={`pli-field__value${value ? '' : ' is-muted'}`}>
              {value ? onFormatDate(value) : t(`${i18nRoot}.parameters.notInformed`)}
            </span>
            <GuideTooltip content={t(`${i18nRoot}.parameters.editButton`)} relationship="label">
              <Button appearance="subtle" size="small" icon={<Edit20Regular />} onClick={() => startEdit(side)} disabled={!!editing} data-testid={`pli-${side}-edit`} />
            </GuideTooltip>
          </div>
        )}
      </div>
    );
  };

  return (
    <section className="pli" aria-label={t('priceListPanel.title')} data-testid="price-list-info">
      <h4 className="pli__title">{t('priceListPanel.title')}</h4>

      {rangeError && (
        <GuideMessageBar intent="error" layout="multiline" data-testid="pli-range-error">
          <MessageBarBody>
            <MessageBarTitle>{t(`${i18nRoot}.attentionTitle`)}</MessageBarTitle>
            {t(`${i18nRoot}.validityError.dateError`)}
          </MessageBarBody>
        </GuideMessageBar>
      )}

      {renderDateField('start', t(`${i18nRoot}.validSince`), start, t(`${i18nRoot}.parameters.validityStart`))}
      {renderDateField('end', t(`${i18nRoot}.validUntil`), end, t(`${i18nRoot}.parameters.endOfValidity`))}

      <div className="pli-field">
        <span className="pli-field__label">{t(`${i18nRoot}.state`)}</span>
        <div className="pli-field__row">
          <StatusPill tone={status.tone} symbol={status.symbol}>{t(status.i18nKey)}</StatusPill>
        </div>
      </div>

      <div className="pli-field">
        <span className="pli-field__label">{t(`${i18nRoot}.expiration`)}</span>
        <div className="pli-field__row">
          <span className={`pli-field__value${days === undefined ? ' is-muted' : days < 0 ? ' is-danger' : days <= 30 ? ' is-warning' : ''}`} data-testid="pli-expiration">
            {expirationText}
          </span>
        </div>
      </div>
    </section>
  );
};

export default PriceListInfoPanel;
